/* dev-blog-listing — client-side filter / pagination.

   Architecture: server pre-renders every article card (capped at 100) into
   the DOM with data-id, data-tags, data-card-index, data-published-at.
   This controller owns three pieces of state — `tag`, `tab`, `page` — reads
   them from the URL on load, applies them by hiding/showing cards, builds
   pagination markup, and syncs state to the URL via history.pushState (no
   full reload). Browser back/forward via popstate.

   Why client-side and not AJAX section reload: Shopify Liquid can't read
   arbitrary query params (only `?page=` via paginate), so `?tab=` and
   `?filter_tag=` would have to be path-based. Client-side sidesteps that
   constraint, and the blogs this section is designed for (small/medium)
   easily fit in the pre-render budget.

   Tabs are merchant-defined blocks. Each tab carries a tag handle in
   `data-tab-tag` (empty for a "show all" tab). The first tab is flagged
   `data-tab-default="true"` and is active when no `?tab=` is in the URL.
   Tab and dropdown compose by intersection — both tags must be present on
   a card for it to show.

   Featured bento visible only when state is fully default (default tab
   active, no dropdown tag, page=1). Otherwise hidden, and the featured
   article re-enters the grid pool (shown if it matches the filter). */

(function () {
  'use strict';

  var ROOT_SELECTOR = '.dev-blog-listing[data-dev-blog-listing]';

  function DevBlogListing(root) {
    this.root = root;
    this.grid = root.querySelector('[data-dev-bl-grid]');
    this.cards = this.grid ? Array.prototype.slice.call(this.grid.querySelectorAll('.dev-blog-listing__card')) : [];
    this.featured = root.querySelector('[data-dev-bl-featured]');
    this.filterRow = root.querySelector('.dev-blog-listing__filter-row');
    this.tabsWrap = root.querySelector('[data-dev-bl-tabs]');
    this.tabs = this.tabsWrap ? Array.prototype.slice.call(this.tabsWrap.querySelectorAll('.dev-blog-listing__tab')) : [];
    this.select = root.querySelector('.dev-blog-listing__select');
    this.paginationContainer = root.querySelector('[data-dev-bl-pagination]');
    this.emptyEl = root.querySelector('[data-dev-bl-empty]');
    this.iconLeft = this.readTemplate('arrow-left');
    this.iconRight = this.readTemplate('arrow-right');

    this.featuredId = root.getAttribute('data-featured-id') || '';
    this.perPage = parseInt(root.getAttribute('data-articles-per-page'), 10) || 6;
    this.showPagination = root.getAttribute('data-show-pagination') === 'true';
    this.initialTag = (root.getAttribute('data-initial-tag') || '').toLowerCase();

    this.tabTags = this.tabs.map(function (t) {
      return (t.getAttribute('data-tab-tag') || '').toLowerCase();
    });
    var defaultIdx = -1;
    for (var i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].getAttribute('data-tab-default') === 'true') { defaultIdx = i; break; }
    }
    if (defaultIdx === -1 && this.tabs.length) defaultIdx = 0;
    this.defaultTabTag = defaultIdx >= 0 ? this.tabTags[defaultIdx] : '';

    this.state = { tag: '', tab: this.defaultTabTag, page: 1 };
    this.readState();
    this.bind();
    this.render();
  }

  DevBlogListing.prototype.readTemplate = function (name) {
    var tpl = this.root.querySelector('[data-dev-bl-icon-tpl="' + name + '"]');
    return tpl ? tpl.innerHTML : '';
  };

  DevBlogListing.prototype.readState = function () {
    var params = new URLSearchParams(window.location.search);
    var tagParam = params.get('filter_tag');
    if (tagParam !== null) {
      this.state.tag = (tagParam || '').toLowerCase();
    } else if (this.initialTag) {
      this.state.tag = this.initialTag;
    } else {
      this.state.tag = '';
    }
    var tabParam = params.get('tab');
    this.state.tab = (tabParam !== null ? tabParam : this.defaultTabTag).toLowerCase();
    var pageVal = parseInt(params.get('page'), 10);
    this.state.page = (isFinite(pageVal) && pageVal > 0) ? pageVal : 1;
  };

  DevBlogListing.prototype.bind = function () {
    var self = this;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        var tabTag = (tab.getAttribute('data-tab-tag') || '').toLowerCase();
        if (tabTag === self.state.tab) return;
        self.state.tab = tabTag;
        self.state.page = 1;
        self.update({ scroll: false });
      });
    });

    if (this.select) {
      this.select.addEventListener('change', function () {
        self.state.tag = (self.select.value || '').toLowerCase();
        self.state.page = 1;
        self.update({ scroll: false });
      });
    }

    this.root.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a.dev-blog-listing__page, a.dev-blog-listing__page-arrow');
      if (!link || !self.root.contains(link)) return;
      var page = parseInt(link.getAttribute('data-page') || '', 10);
      if (!isFinite(page) || page < 1) return;
      e.preventDefault();
      self.state.page = page;
      self.update({ scroll: true });
    });

    window.addEventListener('popstate', function () {
      self.readState();
      self.render();
    });
  };

  DevBlogListing.prototype.update = function (opts) {
    this.render();
    this.pushURL();
    if (opts && opts.scroll) this.scrollToTop();
  };

  DevBlogListing.prototype.scrollToTop = function () {
    var anchor = this.filterRow || this.root;
    var rect = anchor.getBoundingClientRect();
    var top = rect.top + window.pageYOffset - 80;
    window.scrollTo({ top: top, behavior: 'smooth' });
  };

  DevBlogListing.prototype.pushURL = function () {
    var params = new URLSearchParams(window.location.search);
    if (this.state.tag) params.set('filter_tag', this.state.tag); else params.delete('filter_tag');
    if (this.state.tab && this.state.tab !== this.defaultTabTag) params.set('tab', this.state.tab); else params.delete('tab');
    if (this.state.page > 1) params.set('page', String(this.state.page)); else params.delete('page');
    var qs = params.toString();
    var newURL = window.location.pathname + (qs ? '?' + qs : '');
    history.pushState({ state: this.state }, '', newURL);
  };

  DevBlogListing.prototype.isDefaultState = function () {
    return !this.state.tag && this.state.tab === this.defaultTabTag;
  };

  DevBlogListing.prototype.cardTags = function (card) {
    var raw = card.getAttribute('data-tags') || '';
    if (!raw) return [];
    return raw.split(',');
  };

  DevBlogListing.prototype.computeVisible = function () {
    var self = this;
    var items = this.cards.slice();

    if (this.isDefaultState() && this.featuredId && this.featured) {
      items = items.filter(function (c) {
        return c.getAttribute('data-id') !== self.featuredId;
      });
    }

    if (this.state.tag) {
      items = items.filter(function (c) {
        return self.cardTags(c).indexOf(self.state.tag) !== -1;
      });
    }

    if (this.state.tab) {
      items = items.filter(function (c) {
        return self.cardTags(c).indexOf(self.state.tab) !== -1;
      });
    }

    return items;
  };

  DevBlogListing.prototype.render = function () {
    var items = this.computeVisible();
    var total = items.length;
    var totalPages = Math.max(1, Math.ceil(total / this.perPage));

    if (this.state.page > totalPages) this.state.page = totalPages;
    if (this.state.page < 1) this.state.page = 1;

    var start = (this.state.page - 1) * this.perPage;
    var end = start + this.perPage;
    var visible = items.slice(start, end);
    var visibleSet = new Set(visible);

    this.cards.forEach(function (c) {
      if (visibleSet.has(c)) {
        c.classList.remove('is-hidden');
      } else {
        c.classList.add('is-hidden');
      }
    });

    if (this.grid) {
      visible.forEach(function (c) {
        // appendChild moves the existing node, so DOM order matches sort order
        this.grid.appendChild(c);
      }, this);
    }

    if (this.featured) {
      if (this.isDefaultState()) {
        this.featured.classList.remove('is-hidden');
      } else {
        this.featured.classList.add('is-hidden');
      }
    }

    if (this.emptyEl) {
      if (total === 0) {
        this.emptyEl.classList.remove('is-hidden');
      } else {
        this.emptyEl.classList.add('is-hidden');
      }
    }

    var curTab = this.state.tab;
    this.tabs.forEach(function (t) {
      var tabTag = (t.getAttribute('data-tab-tag') || '').toLowerCase();
      t.classList.toggle('is-active', tabTag === curTab);
    });

    if (this.select && this.select.value !== this.state.tag) {
      this.select.value = this.state.tag;
    }

    this.renderPagination(totalPages);
  };

  DevBlogListing.prototype.renderPagination = function (totalPages) {
    if (!this.paginationContainer) return;
    if (!this.showPagination || totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      return;
    }

    var cur = this.state.page;
    var parts = this.computeParts(cur, totalPages);
    var prevDisabled = cur <= 1;
    var nextDisabled = cur >= totalPages;

    var html = '<nav class="dev-blog-listing__pagination" role="navigation" aria-label="Pagination">';
    html += prevDisabled
      ? '<span class="dev-blog-listing__page-arrow is-disabled" aria-hidden="true">' + this.iconLeft + '</span>'
      : '<a class="dev-blog-listing__page-arrow" href="' + this.pageHref(cur - 1) + '" data-page="' + (cur - 1) + '" aria-label="Previous">' + this.iconLeft + '</a>';

    html += '<ul class="dev-blog-listing__pages" role="list">';
    parts.forEach(function (p) {
      if (p === '...') {
        html += '<li><span class="dev-blog-listing__page is-ellipsis" aria-hidden="true">…</span></li>';
      } else if (p === cur) {
        html += '<li><span class="dev-blog-listing__page is-current" aria-current="page">' + p + '</span></li>';
      } else {
        html += '<li><a class="dev-blog-listing__page" href="' + this.pageHref(p) + '" data-page="' + p + '">' + p + '</a></li>';
      }
    }, this);
    html += '</ul>';

    html += nextDisabled
      ? '<span class="dev-blog-listing__page-arrow is-next is-disabled" aria-hidden="true">' + this.iconRight + '</span>'
      : '<a class="dev-blog-listing__page-arrow is-next" href="' + this.pageHref(cur + 1) + '" data-page="' + (cur + 1) + '" aria-label="Next">' + this.iconRight + '</a>';

    html += '</nav>';
    this.paginationContainer.innerHTML = html;
  };

  DevBlogListing.prototype.pageHref = function (n) {
    var params = new URLSearchParams(window.location.search);
    if (this.state.tag) params.set('filter_tag', this.state.tag); else params.delete('filter_tag');
    if (this.state.tab && this.state.tab !== this.defaultTabTag) params.set('tab', this.state.tab); else params.delete('tab');
    if (n > 1) params.set('page', String(n)); else params.delete('page');
    var qs = params.toString();
    return qs ? '?' + qs : window.location.pathname;
  };

  DevBlogListing.prototype.computeParts = function (cur, total) {
    var parts = [];
    if (total <= 7) {
      for (var i = 1; i <= total; i++) parts.push(i);
      return parts;
    }
    parts.push(1);
    if (cur > 4) parts.push('...');
    var lo = Math.max(2, cur - 1);
    var hi = Math.min(total - 1, cur + 1);
    for (var j = lo; j <= hi; j++) parts.push(j);
    if (cur < total - 3) parts.push('...');
    parts.push(total);
    return parts;
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    roots.forEach(function (root) {
      if (root.getAttribute('data-dev-bl-inited') === 'true') return;
      root.setAttribute('data-dev-bl-inited', 'true');
      try { new DevBlogListing(root); } catch (e) { /* swallow init errors */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target;
    if (!target) return;
    if (target.matches && target.matches(ROOT_SELECTOR)) {
      target.removeAttribute('data-dev-bl-inited');
      init(target.parentNode);
    } else {
      init(target);
    }
  });
})();
